'use client';

import * as React from 'react';
import { useId, useMemo } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import {
  TooltipProvider as ShadcnTooltipProvider,
  TooltipTrigger as ShadcnTooltipTrigger,
  TooltipContent as ShadcnTooltipContent,
  Tooltip as ShadcnTooltip,
} from '@/hen-ui/shadcn/tooltip';
import {
  resolveTooltipBody,
  type ResolvedTooltipBody,
  type TooltipContentFormat,
} from './tooltip-content';
import { useTooltipTrigger } from './use-tooltip-trigger';
import { useNoHoverPointer } from './use-no-hover-pointer';
import {
  getExclusiveTriggerSlotProps,
  getOnlyChildElement,
  isChildAlreadyKeyboardFocusable,
  isChildNativeFocusable,
  type TooltipTriggerMode,
} from './tooltip-trigger.utils';

type ShadcnTooltipRootProps = React.ComponentPropsWithoutRef<
  typeof ShadcnTooltip
>;
type ShadcnTooltipContentProps = React.ComponentPropsWithoutRef<
  typeof ShadcnTooltipContent
>;

export type { TooltipContentFormat, TooltipTriggerMode };

export interface TooltipProps
  extends Omit<ShadcnTooltipRootProps, 'children'>,
    Omit<ShadcnTooltipContentProps, 'children'> {
  content: string;
  children: React.ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Defaults to `plain`. Markdown supports `**bold**`, `*italic*`, and `[text](url)`. */
  contentFormat?: TooltipContentFormat;
  /** Defaults to `hover` (Radix pointer + focus). `click` and `focus` use controlled open. */
  trigger?: TooltipTriggerMode;
}

const CONTENT_BODY_CLASS = '[&_a]:underline [&_p]:m-0 [&_p+p]:mt-1';
const TRIGGER_WRAPPER_CLASS = 'inline-flex w-fit self-start';

function TooltipBody({ body }: { body: ResolvedTooltipBody }) {
  if (body.format === 'markdown') {
    return <div className={CONTENT_BODY_CLASS}>{body.nodes}</div>;
  }
  return <>{body.text}</>;
}

export const Tooltip = ({
  content,
  children,
  side,
  contentFormat = 'plain',
  trigger = 'hover',
  open: openProp,
  defaultOpen,
  onOpenChange,
  delayDuration = 0,
  disableHoverableContent,
  ...contentProps
}: TooltipProps) => {
  const descriptionId = useId();
  const body = useMemo(
    () => resolveTooltipBody(content, contentFormat),
    [content, contentFormat],
  );

  // Touch devices cannot hover reliably, so default hover tooltips use the
  // tap-to-toggle path while desktop hover and explicit triggers stay unchanged.
  const noHoverPointer = useNoHoverPointer();
  const effectiveTrigger =
    trigger === 'hover' && noHoverPointer ? 'click' : trigger;

  const {
    usesRadixHover,
    open,
    onOpenChange: rootOnOpenChange,
    exclusiveHandlers,
    contentDismissProps,
  } = useTooltipTrigger({
    mode: effectiveTrigger,
    open: openProp,
    defaultOpen,
    onOpenChange,
  });

  const providerDelay = usesRadixHover ? delayDuration : 0;
  const ariaDescribedBy = !usesRadixHover && open ? descriptionId : undefined;

  const tooltipContent = (
    <ShadcnTooltipContent
      id={descriptionId}
      side={side}
      data-hen-id={COMPONENT_IDS.Tooltip}
      {...contentDismissProps}
      {...contentProps}
    >
      <TooltipBody body={body} />
    </ShadcnTooltipContent>
  );

  const renderTrigger = () => {
    if (usesRadixHover) {
      if (isChildNativeFocusable(children)) {
        const only = getOnlyChildElement(children);
        if (only) {
          return <ShadcnTooltipTrigger asChild>{only}</ShadcnTooltipTrigger>;
        }
      }
      if (isChildAlreadyKeyboardFocusable(children)) {
        const only = getOnlyChildElement(children);
        if (only) {
          return <ShadcnTooltipTrigger asChild>{only}</ShadcnTooltipTrigger>;
        }
      }
      const needsTabStop = !isChildAlreadyKeyboardFocusable(children);
      return (
        <ShadcnTooltipTrigger asChild>
          <span
            className={TRIGGER_WRAPPER_CLASS}
            {...(needsTabStop ? { tabIndex: 0 } : {})}
          >
            {children}
          </span>
        </ShadcnTooltipTrigger>
      );
    }

    if (isChildNativeFocusable(children)) {
      const only = getOnlyChildElement(children);
      if (!only || !exclusiveHandlers) {
        return children;
      }
      return (
        <ShadcnTooltipTrigger asChild>
          <Slot
            {...getExclusiveTriggerSlotProps(only, exclusiveHandlers, {
              ariaDescribedBy,
            })}
          >
            {only}
          </Slot>
        </ShadcnTooltipTrigger>
      );
    }

    if (isChildAlreadyKeyboardFocusable(children)) {
      const only = getOnlyChildElement(children);
      if (!only || !exclusiveHandlers) {
        return children;
      }
      return (
        <ShadcnTooltipTrigger asChild>
          <Slot
            {...getExclusiveTriggerSlotProps(only, exclusiveHandlers, {
              ariaDescribedBy,
            })}
          >
            {only}
          </Slot>
        </ShadcnTooltipTrigger>
      );
    }

    return (
      <ShadcnTooltipTrigger asChild>
        <span
          className={TRIGGER_WRAPPER_CLASS}
          tabIndex={0}
          {...exclusiveHandlers}
          aria-describedby={ariaDescribedBy}
        >
          {children}
        </span>
      </ShadcnTooltipTrigger>
    );
  };

  return (
    <ShadcnTooltipProvider delayDuration={providerDelay}>
      <ShadcnTooltip
        // Remount when switching between Radix hover and controlled modes.
        key={usesRadixHover ? 'hover' : 'controlled'}
        {...(usesRadixHover
          ? {
              ...(openProp !== undefined ? { open: openProp } : {}),
              ...(defaultOpen !== undefined ? { defaultOpen } : {}),
              ...(onOpenChange ? { onOpenChange } : {}),
            }
          : { open, onOpenChange: rootOnOpenChange })}
        delayDuration={providerDelay}
        disableHoverableContent={disableHoverableContent}
      >
        {renderTrigger()}
        {tooltipContent}
      </ShadcnTooltip>
    </ShadcnTooltipProvider>
  );
};
